import { CHART_TITLES, ChartData, ChartKind, renderChart } from './charts/index.js';

export type FullscreenContext = ChartData;

const RESIZE_DEBOUNCE_MS = 150;
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab / Shift+Tab inside `root` while the dialog is open. */
export function trapFocus(root: HTMLElement, event: KeyboardEvent): void {
  if (event.key !== 'Tab') return;
  const controls = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (controls.length === 0) return;
  const first = controls[0];
  const last = controls[controls.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !root.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !root.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}

function isChartKind(value: string | null | undefined): value is ChartKind {
  return value === 'balance' || value === 'payment' || value === 'rate';
}

/** Fullscreen modal that renders the selected chart directly in `full` mode (no cloning). */
export function initFullscreen(getContext: () => FullscreenContext | null): void {
  const modal = document.getElementById('chart-fullscreen-modal');
  const closeBtn = document.getElementById('fullscreen-close');
  const titleEl = document.getElementById('fullscreen-title');
  const contentEl = document.getElementById('fullscreen-content');
  if (!modal || !closeBtn || !titleEl || !contentEl) return;

  let activeKind: ChartKind | null = null;
  let opener: HTMLElement | null = null;
  let resizeTimer: ReturnType<typeof setTimeout> | null = null;

  const render = () => {
    if (!activeKind) return;
    const context = getContext();
    const canvas = document.createElement('div');
    canvas.className = 'chart-canvas';
    contentEl.replaceChildren(canvas);
    if (!context) return;
    renderChart(activeKind, canvas, context, 'full');
  };

  const open = (kind: ChartKind, trigger: HTMLElement | null) => {
    activeKind = kind;
    opener = trigger;
    titleEl.textContent = CHART_TITLES[kind];
    modal.classList.add('active');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    render();
    closeBtn.focus();
  };

  const close = () => {
    if (!activeKind) return;
    activeKind = null;
    modal.classList.remove('active');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    contentEl.replaceChildren();
    opener?.focus();
    opener = null;
  };

  document.querySelectorAll<HTMLElement>('.fullscreen-btn[data-chart]').forEach(button => {
    button.addEventListener('click', () => {
      const kind = button.dataset.chart;
      if (isChartKind(kind)) open(kind, button);
    });
  });

  closeBtn.addEventListener('click', close);
  // `.fullscreen-content` covers the whole overlay, so a click on it (outside the canvas)
  // counts as a backdrop click too.
  modal.addEventListener('click', event => {
    if (event.target === modal || event.target === contentEl) close();
  });
  document.addEventListener('keydown', event => {
    if (!activeKind) return;
    if (event.key === 'Escape') close();
    else trapFocus(modal, event);
  });
  window.addEventListener('resize', () => {
    if (!activeKind) return;
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resizeTimer = null;
      render();
    }, RESIZE_DEBOUNCE_MS);
  });
}
